import { NoteType, ResponsiveNoteTemplateVisitType } from "@rebuild/shared";
import { EstimateType } from "../CreatorTools/MappingTool/utils/EstimateType";

export type ResponsiveNoteTemplateType = "general" | "room";

export interface TemplateSectionDraft {
	id?: string;
	title: string;
	position: number;
	context: string;
	followUpPrompt?: string;
	skipFollowUpGeneration?: boolean;
	templateId?: string;
}

export interface ResponsiveNoteTemplateDraft {
	id?: string;
	type: ResponsiveNoteTemplateType;
	noteType: NoteType;
	visitType: ResponsiveNoteTemplateVisitType;
	estimateType?: EstimateType;
	organizationId?: string;
	createdBy?: string;
	updatedBy?: string;
	createdAt?: number;
	updatedAt?: number;
	sections: TemplateSectionDraft[];
}

export interface ResponsiveNoteTemplate extends ResponsiveNoteTemplateDraft {
	id: string;
	organizationId: string;
	createdBy: string;
	updatedBy: string;
	createdAt: number;
	updatedAt: number;
	sections: Array<TemplateSectionDraft & { id: string; templateId: string }>;
}

export interface SaveResponsiveNoteTemplateBody {
	id?: string;
	type: ResponsiveNoteTemplateType;
	noteType: NoteType;
	visitType: ResponsiveNoteTemplateVisitType;
	sections: Array<{
		id?: string;
		title: string;
		position: number;
		context: string;
		followUpPrompt: string;
		skipFollowUpGeneration: boolean;
	}>;
}

export interface ResponsiveNoteTemplateOrganization {
	organizationId: string;
	organizationName?: string;
}

export function getPreferredResponsiveTemplateOrganization<T extends ResponsiveNoteTemplateOrganization>({
	organizations,
	selectedOrganization,
	preferredOrganizationId,
}: {
	organizations: T[];
	selectedOrganization?: T;
	preferredOrganizationId?: string;
}): T | undefined {
	if (
		selectedOrganization?.organizationId &&
		organizations.some((organization) => organization.organizationId === selectedOrganization.organizationId)
	) {
		return selectedOrganization;
	}
	return organizations.find((organization) => organization.organizationId === preferredOrganizationId) ?? organizations[0];
}

export function createDraftTemplate(
	type: ResponsiveNoteTemplateType,
	noteType: NoteType,
	visitType: ResponsiveNoteTemplateVisitType = ResponsiveNoteTemplateVisitType.INITIAL,
): ResponsiveNoteTemplateDraft {
	return {
		type,
		noteType,
		visitType,
		sections: [],
	};
}

export function getTemplateForType(
	templates: ResponsiveNoteTemplateDraft[],
	type: ResponsiveNoteTemplateType,
): ResponsiveNoteTemplateDraft | undefined {
	return templates.find((template) => template.type === type);
}

export function buildTemplateSaveBody(template: ResponsiveNoteTemplateDraft): SaveResponsiveNoteTemplateBody {
	return {
		id: template.id,
		type: template.type,
		noteType: template.noteType,
		visitType: template.visitType,
		sections: [...template.sections]
			.sort((a, b) => a.position - b.position)
			.map((section, index) => ({
				id: section.id,
				title: section.title,
				position: index,
				context: section.context,
				followUpPrompt: section.followUpPrompt ?? "",
				skipFollowUpGeneration: section.skipFollowUpGeneration ?? false,
			})),
	};
}
